import * as Yup from "yup";

/**
 * Required-field validation for the job offer form ("Opublikuj ofertę" flow).
 * "Zapisz szkic" skips this schema entirely and submits immediately.
 */
export const jobOfferValidationSchema = Yup.object({
    basicInfo: Yup.object({
        positionName: Yup.string().trim().required("Required"),
        companyDescription: Yup.string().max(5000, "Maksymalna długość pola wynosi 5000 znaków."),
        location: Yup.string().trim().required("Required"),
        workMode: Yup.string().required("Required"),
        offerStatus: Yup.string().required("Required"),
        notificationEmails: Yup.array().when("sendNotifications", {
            is: true,
            then: (schema) => schema.min(1, "Dodaj co najmniej jeden adres e-mail do powiadomień."),
        }),
    }),
    positionDescription: Yup.object({
        shortDescription: Yup.string().trim().required("Required"),
        employeeOnboarding: Yup.string().max(5000, "Maksymalna długość pola wynosi 5000 znaków."),
        mainResponsibilities: Yup.array()
            .of(Yup.string())
            .test("at-least-one-filled", "Dodaj co najmniej 1 obowiązek", (arr) => Array.isArray(arr) && arr.some((v) => v && v.trim() !== "")),
    }),

    employmentForms: Yup.array()
        .min(1, "Dodaj co najmniej jedną formę zatrudnienia")
        .of(
            Yup.object({
                type: Yup.array().min(1, "Required"),
                preferredType: Yup.string().required("Required"),
                aiQuestion: Yup.string().trim().required("Required"),
            })
        ),
    salaryModels: Yup.array().of(
        Yup.object({
            type: Yup.string().required("Required"),
            currency: Yup.string().required("Required"),
        })
    ),
    bonuses: Yup.array().of(
        Yup.object({
            name: Yup.string().trim().required("Required"),
            type: Yup.string().required("Required"),
        })
    ),
    allowances: Yup.array().of(
        Yup.object({
            name: Yup.string().trim().required("Required"),
            type: Yup.string().required("Required"),
        })
    ),
    workingTime: Yup.object({
        workloadType: Yup.string().required("Required"),
        workSystem: Yup.string().required("Required"),
        overtime: Yup.object({
            frequency: Yup.string().nullable().when("exists", {
                is: true,
                then: (schema) => schema.required("Required"),
            }),
            settlementMethod: Yup.string().nullable().when("exists", {
                is: true,
                then: (schema) => schema.required("Required"),
            }),
        }),
    }),

    riskFactors: Yup.array().of(
        Yup.object({
            name: Yup.string().trim().required("Required"),
        })
    ),
    protectiveEquipment: Yup.array().of(
        Yup.object({
            name: Yup.string().trim().required("Required"),
        })
    ),

    requiredDocuments: Yup.array().of(
        Yup.object({
            name: Yup.string().trim().required("Required"),
            status: Yup.string().required("Required"),
        })
    ),
    skills: Yup.array().of(
        Yup.object({
            name: Yup.string().trim().required("Required"),
            category: Yup.string().required("Required"),
            status: Yup.string().required("Required"),
            confirmationType: Yup.string().when("requiresConfirmation", {
                is: true,
                then: (schema) => schema.required("Required"),
            }),
        })
    ),
    experienceRequirements: Yup.array().of(
        Yup.object({
            area: Yup.string().trim().required("Required"),
            status: Yup.string().required("Required"),
        })
    ),

    customQuestions: Yup.array().of(
        Yup.object({
            questionText: Yup.string().trim().required("Required"),
            answerType: Yup.string().required("Required"),
        })
    ),
    motivationQuestions: Yup.array().of(
        Yup.object({
            questionText: Yup.string().trim().required("Required"),
        })
    ),
    financialExpectations: Yup.object({
        currency: Yup.string().required("Required"),
    }),
    availability: Yup.object({
        earliestAcceptedStartDate: Yup.string().when("aiAsksAboutAvailability", {
            is: true,
            then: (schema) => schema.required("Required"),
        }),
        maxNoticePeriodDays: Yup.number().nullable().when("aiAsksAboutAvailability", {
            is: true,
            then: (schema) => schema.required("Required"),
        }),
    }),
    disqualificationRules: Yup.array().of(
        Yup.object({
            name: Yup.string().trim().required("Required"),
            conditionDescription: Yup.string().trim().required("Required"),
        })
    ),
    aiConversationConfig: Yup.object({
        conversationStyle: Yup.string().required("Required"),
        languages: Yup.array().of(Yup.string()).min(1, "Required"),
    }),
});

/**
 * Maps each wizard step id to the top-level formik error keys that belong to it,
 * so the UI can flag which step needs attention on a failed submit.
 */
export const STEP_ERROR_FIELDS = {
    1: ["basicInfo", "positionDescription"],
    2: ["employmentForms", "salaryModels", "bonuses", "allowances", "workingTime"],
    3: ["riskFactors", "protectiveEquipment"],
    4: ["requiredDocuments", "skills", "experienceRequirements"],
    5: ["customQuestions", "motivationQuestions", "financialExpectations", "availability", "disqualificationRules", "aiConversationConfig"],
};

export const stepHasErrors = (stepId, errors) => (STEP_ERROR_FIELDS[stepId] || []).some((field) => Boolean(errors[field]));
